<template>
  <div class="user-avatar-wrapper">
    <button
      v-if="clickable"
      type="button"
      class="user-avatar user-avatar--button"
      :class="`user-avatar--${size}`"
      :style="containerStyle"
      :title="clickTitle"
      :aria-label="ariaLabel"
      @click="openAnimalModal"
    >
      <img
        v-if="showImage"
        :src="resolvedAvatarUrl"
        :alt="imageAlt"
        class="user-avatar__image"
        @error="onImageError"
      />
      <span v-else class="user-avatar__initials">{{ initials }}</span>
    </button>

    <div
      v-else
      class="user-avatar"
      :class="`user-avatar--${size}`"
      :style="containerStyle"
      :title="avatarTitle"
      role="img"
      :aria-label="ariaLabel"
    >
      <img
        v-if="showImage"
        :src="resolvedAvatarUrl"
        :alt="imageAlt"
        class="user-avatar__image"
        @error="onImageError"
      />
      <span v-else class="user-avatar__initials">{{ initials }}</span>
    </div>

    <animal-keyword-photo-modal
      :show.sync="showAnimalModal"
      :keyword="resolvedAnimalKeyword"
      :photo-url="animalPhotoMediumUrl || animalPhotoUrl"
      :attribution="animalPhotoAttribution"
      :common-name="animalCommonName"
      :scientific-name="animalScientificName"
    />
  </div>
</template>

<script>
import AnimalKeywordPhotoModal from '@/components/layout/AnimalKeywordPhotoModal.vue';
import { getCachedAnimalPhoto, fetchAnimalPhotoByKeyword } from '@/lib/inaturalistAnimalPhoto';
import {
  getUserAvatarColor,
  getUserAvatarSeed,
  getUserAvatarUrl,
  getUserDisplayName,
  getUserInitials,
  getUserPalavraChave2FA,
} from '@/lib/userProfile';

export default {
  name: 'UserAvatar',
  components: {
    AnimalKeywordPhotoModal,
  },
  props: {
    user: {
      type: Object,
      default: null,
    },
    name: {
      type: String,
      default: '',
    },
    avatarUrl: {
      type: String,
      default: '',
    },
    animalKeyword: {
      type: String,
      default: '',
    },
    seed: {
      type: String,
      default: '',
    },
    size: {
      type: String,
      default: 'sm',
      validator: (value) => ['xs', 'sm', 'md', 'lg'].includes(value),
    },
    clickable: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      imageFailed: false,
      animalPhotoUrl: '',
      animalPhotoMediumUrl: '',
      animalPhotoAttribution: '',
      animalCommonName: '',
      animalScientificName: '',
      loadingAnimalPhoto: false,
      showAnimalModal: false,
    };
  },
  computed: {
    displayName() {
      return this.name || getUserDisplayName(this.user);
    },
    resolvedAnimalKeyword() {
      return this.animalKeyword || getUserPalavraChave2FA(this.user);
    },
    explicitAvatarUrl() {
      return this.avatarUrl || getUserAvatarUrl(this.user);
    },
    resolvedAvatarUrl() {
      return this.explicitAvatarUrl || this.animalPhotoUrl;
    },
    resolvedSeed() {
      return this.seed || getUserAvatarSeed(this.user);
    },
    initials() {
      return getUserInitials(this.displayName);
    },
    showImage() {
      return Boolean(this.resolvedAvatarUrl) && !this.imageFailed;
    },
    containerStyle() {
      if (this.showImage) {
        return undefined;
      }

      return {
        background: getUserAvatarColor(this.resolvedSeed),
      };
    },
    imageAlt() {
      if (this.animalPhotoUrl && this.animalCommonName) {
        return `Animal ${this.animalCommonName}`;
      }

      return this.displayName;
    },
    ariaLabel() {
      if (this.clickable) {
        return 'Abrir perfil e configurações';
      }

      if (this.animalPhotoUrl && this.animalCommonName) {
        return `Avatar com foto de ${this.animalCommonName}`;
      }

      return `Avatar de ${this.displayName}`;
    },
    avatarTitle() {
      if (this.animalPhotoAttribution) {
        return this.animalPhotoAttribution;
      }

      return this.displayName;
    },
    clickTitle() {
      return `Abrir perfil`;
    },
  },
  watch: {
    resolvedAvatarUrl() {
      this.imageFailed = false;
    },
    resolvedAnimalKeyword: {
      immediate: true,
      handler(keyword) {
        this.loadAnimalPhoto(keyword);
      },
    },
    user: {
      deep: true,
      handler() {
        this.loadAnimalPhoto(this.resolvedAnimalKeyword);
      },
    },
  },
  methods: {
    openAnimalModal() {
      if (!this.clickable) {
        return;
      }

      this.showAnimalModal = true;
    },
    async loadAnimalPhoto(keyword) {
      if (this.explicitAvatarUrl || !keyword) {
        this.animalPhotoUrl = '';
        this.animalPhotoMediumUrl = '';
        this.animalPhotoAttribution = '';
        this.animalCommonName = '';
        this.animalScientificName = '';
        return;
      }

      const cached = getCachedAnimalPhoto(keyword);
      if (cached?.url) {
        this.applyAnimalPhoto(cached);
        return;
      }

      this.loadingAnimalPhoto = true;

      try {
        const photo = await fetchAnimalPhotoByKeyword(keyword);
        if (photo?.url && keyword === this.resolvedAnimalKeyword) {
          this.applyAnimalPhoto(photo);
        }
      } catch (error) {
        console.error('Erro ao carregar foto do animal no iNaturalist:', error);
      } finally {
        this.loadingAnimalPhoto = false;
      }
    },
    applyAnimalPhoto(photo) {
      this.animalPhotoUrl = photo.url;
      this.animalPhotoMediumUrl = photo.mediumUrl || photo.url;
      this.animalPhotoAttribution = photo.attribution || '';
      this.animalCommonName = photo.commonName || '';
      this.animalScientificName = photo.scientificName || '';
      this.imageFailed = false;
    },
    onImageError() {
      this.imageFailed = true;
      this.animalPhotoUrl = '';
      this.animalPhotoMediumUrl = '';
    },
  },
};
</script>

<style scoped>
.user-avatar-wrapper {
  display: inline-flex;
  flex-shrink: 0;
}

.user-avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  border-radius: 50%;
  overflow: hidden;
  color: #fff;
  font-weight: var(--font-weight-semibold);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.18);
}

.user-avatar--button {
  border: none;
  padding: 0;
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.user-avatar--button:hover,
.user-avatar--button:focus-visible {
  transform: scale(1.06);
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.22);
  outline: none;
}

.user-avatar__image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  pointer-events: none;
}

.user-avatar__initials {
  line-height: 1;
  text-transform: uppercase;
  pointer-events: none;
}

.user-avatar--xs {
  width: 24px;
  height: 24px;
  font-size: 0.62rem;
}

.user-avatar--sm {
  width: 32px;
  height: 32px;
  font-size: 0.75rem;
}

.user-avatar--md {
  width: 40px;
  height: 40px;
  font-size: 0.9rem;
}

.user-avatar--lg {
  width: 48px;
  height: 48px;
  font-size: 1rem;
}
</style>
